import React, { useEffect, useState } from 'react'
import classes from '../styles.module.scss'
import { useShared } from '../../../../contexts/shared.context';
import { ILandFund, ILandFundItem, ILargeLandowners } from '../../../../types/screening.types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useKato } from '../../../../contexts/kato.context';
import { updateScreening } from '../../../../requests/screening.request';
import FooterComponent from '../../components/footer.component';
import Field from '../../../../components/field/field.component';
import { countTotal, isEmpty, t } from '../../../../helpers/utils';
import { PlusIcon, XIcon } from '../../../../assets/icons';
import { toast } from 'react-toastify';

const largeLandownersFields: (keyof ILargeLandowners)[] = [
    'name',
    'total_land',
    'total_arable',
    'total_pasture',
    'fact_land',
    'fact_arable',
    'fact_pasture',
]

const landFundFields: (keyof ILandFundItem)[] = ['settlementLand', 'agricultural_farmEnterprises', 'agricultural_arable', "agricultural_pasture", 'agricultural_total', 'reserve_arable', 'reserve_pasture', 'reserve_total', 'otherLand'];

const baseClass = classes['screening-tab__land-fund'];
const translationKey = 'screening.land-fund';

const LandFundTab = () => {
    const { selectedInfoYear } = useShared();
    const { kato } = useParams<{ kato: string }>();
    const { i18n: { language } } = useTranslation();
    const { screeningInfo: { landFund }, updateScreeningInfo } = useKato();
    const defaultLandFund: ILandFund = {
        largeLandowners: [],
        landFundItems: {
            settlementLand: '',
            agricultural_total: '',
            agricultural_arable: '',
            agricultural_pasture: '',
            agricultural_farmEnterprises: '',
            reserve_total: '',
            reserve_arable: '',
            reserve_pasture: '',
            otherLand: '',
        }
    }

    const [formData, setFormData] = useState<ILandFund>(defaultLandFund);
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [isAdding, setIsAdding] = useState(false);
    const [newLandowner, setNewLandowner] = useState<ILargeLandowners | null>(null);

    const handleSave = () => {
        if (validateFields() && kato) {
            updateScreening(+kato, 'landFund', formData, selectedInfoYear)
                .then(() => {
                    updateScreeningInfo();
                    toast.success(t(`admin.success`, language));
                })
                .catch((error) => {
                    console.error('Error updating land fund:', error);
                });
        }
    };

    const validateFields = () => {
        const newErrors = {} as Record<string, boolean>;
        formData.largeLandowners?.forEach((landowner, index) => {
            largeLandownersFields.forEach(field => {
                if (isEmpty(landowner[field])) {
                    newErrors[`owners-${index}-${field}`] = true;
                }
            });
        });

        landFundFields.forEach(field => {
            if (isEmpty(formData.landFundItems?.[field]) && !field.includes('total')) {
                newErrors[`land-fund-${field}`] = true;
            }
        });
        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    }

    const validateNewLandowner = () => {
        const newErrors = {} as Record<string, boolean>;
        if (!newLandowner) return false;
        largeLandownersFields.forEach(field => {
            if (!isEmpty(newLandowner[field])) {
                newErrors[`new-owner-${field}`] = false;
            }
        });
        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    };

    const handleStartAdd = () => {
        setNewLandowner({
            name: '',
            total_land: 0,
            total_arable: 0,
            total_pasture: 0,
            fact_land: 0,
            fact_arable: 0,
            fact_pasture: 0,
        });
        setIsAdding(true);
    };

    const handleCancelAdd = () => {
        setNewLandowner(null);
        setIsAdding(false);
    };

    const handleSaveNewLandowner = () => {
        if (!validateNewLandowner() || !formData.largeLandowners) return;

        setFormData({
            ...formData,
            largeLandowners: [...formData.largeLandowners, newLandowner!],
        });
        setNewLandowner(null);
        setIsAdding(false);
    };

    const handleDeleteLandowner = (index: number) => {
        if (!formData.largeLandowners) return;

        const updatedLargeLandowners = formData.largeLandowners.filter((_, idx) => idx !== index);
        setFormData({
            ...formData,
            largeLandowners: updatedLargeLandowners,
        });
    }

    useEffect(() => {
        if (landFund) {
            setFormData(landFund || defaultLandFund);
        } else {
            setFormData(defaultLandFund);
        }
    }, [landFund]);

    if (!landFund || !kato) return null;

    const renderFields = (lang: 'ru' | 'kz') => {
        return (
            <div className={classes['fields-container']}>
                {
                    formData.largeLandowners && (
                        <div className={classes['field-block']}>
                            <div className={classes['field-block__title']}>{t(`${translationKey}.large-landowners.title`, lang)}</div>
                            <div className={classes['field-block__fields']}>
                                {formData.largeLandowners.map((item, index) => (
                                    <div className={classes['field-block']} key={index}>
                                        <div className={classes['field-block__delete']} onClick={() => handleDeleteLandowner(index)}><XIcon /></div>
                                        <div className={classes['field-block__fields']}>
                                            {
                                                largeLandownersFields.map((field) => {
                                                    const value = item[field] ?? '';
                                                    return (
                                                        <Field
                                                            key={field}
                                                            label={t(`${translationKey}.large-landowners.${field}`, lang)}
                                                            value={value}
                                                            onChange={(value) => {
                                                                const val = field === 'name' ? value : value === '' ? '' : +value;
                                                                const updatedLargeLandowners = formData.largeLandowners?.map((landowner, idx) =>
                                                                    idx === index ? { ...landowner, [field]: val } : landowner
                                                                ) || [];
                                                                setFormData({
                                                                    ...formData,
                                                                    largeLandowners: updatedLargeLandowners,
                                                                });
                                                            }}
                                                            error={errors[`owners-${index}-${field}`]}
                                                            type={field === 'name' ? 'text' : 'number'}
                                                            required
                                                        />
                                                    )
                                                })
                                            }


                                        </div>
                                    </div>
                                ))}

                                {!isAdding && (
                                    <div className={classes['field-block__actions']}>
                                        <button onClick={handleStartAdd}><PlusIcon /> {t(`actions.add`, lang)}</button>
                                    </div>
                                )}

                                {isAdding && newLandowner && (
                                    <div className={classes['field-block']}>
                                        <div className={classes['field-block__fields']}>
                                            {largeLandownersFields.map((field) => (
                                                <Field
                                                    key={field}
                                                    label={t(`${translationKey}.large-landowners.${field}`, lang)}
                                                    value={newLandowner[field]}
                                                    onChange={(value) => {
                                                        const val = field === 'name' ? value : +value;
                                                        setNewLandowner({ ...newLandowner, [field]: val });
                                                    }}
                                                    error={errors[`new-owner-${field}`]}
                                                    type={field === 'name' ? 'text' : 'number'}
                                                    required
                                                />
                                            ))}
                                        </div>
                                        <div className={classes['field-block__actions']}>
                                            <button onClick={handleSaveNewLandowner}>{t(`actions.save`, lang)}</button>
                                            <button onClick={handleCancelAdd}>{t(`actions.cancel`, lang)}</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }

                {
                    formData.landFundItems !== null && (
                        <div className={classes['field-block']}>
                            <div className={classes['field-block__title']}>{t(`${translationKey}.land-fund-items.title`, lang)}</div>
                            <div className={classes['field-block__fields']}>
                                {
                                    landFundFields.map(field => {
                                        const value = formData.landFundItems?.[field] ?? '';
                                        const val = field.includes('total')
                                            ? formData.landFundItems && countTotal(field.split('_')[0] as 'reserve' | 'agricultural', formData.landFundItems)
                                            : value === ''
                                                ? ''
                                                : value;
                                        return (
                                            <Field
                                                key={field}
                                                label={t(`${translationKey}.land-fund-items.${field}`, lang)}
                                                value={val ?? ''}
                                                onChange={(value) => {
                                                    setFormData({
                                                        ...formData,
                                                        landFundItems: {
                                                            ...formData.landFundItems as ILandFundItem,
                                                            [field]: value === '' ? '' : +value,
                                                        },
                                                    });
                                                }}
                                                error={errors[`land-fund-${field}`]}
                                                type={'number'}
                                                required
                                                disabled={field.includes('_total')}
                                            />
                                        )
                                    })}
                            </div>
                        </div>
                    )
                }
            </div>
        )
    }

    return (
        <div className={classes[baseClass]}>
            <div className={classes[`grid`]}>
                {renderFields(language as 'ru' | 'kz')}
                {renderFields(language === 'ru' ? 'kz' : 'ru')}
            </div>

            <FooterComponent
                onSave={handleSave}
                validateFields={validateFields}
            />
        </div>
    )
}

export default LandFundTab